/*
 * Single source of truth for the four Agentlet archetypes and the
 * "New Agentlet" create menu.
 *
 * WHY THIS EXISTS
 * ---------------
 * The archetype list (label / description / icon / RBAC gate / target flow)
 * used to be hand-copied into every create entry point — the Outcomes page,
 * the Agentlets header, the Agentlets empty-state, the Governance Hub, the GEL
 * page. Each copy drifted: Outcomes said "Auto-create from policy" while
 * Agentlets said "Auto-create from runbook / workflow / URL / docs"; Outcomes
 * hard-disabled the SysLet tile while Agentlets gated it on isSuperAdmin();
 * Outcomes routed ExteLet to /gel-agents while Agentlets opened it in place.
 * Every "the +New menu does the wrong thing" bug traced back to one of these
 * divergent copies. Consolidating the taxonomy + the menu builder + the
 * canonical create route here means there is exactly ONE place to change.
 */
import React from 'react';
import { Sparkles, Bot, Globe, Cpu } from 'lucide-react';

/** The four archetypes, in menu order. `requiresSuperAdmin` gates SysLet. */
export const AGENT_ARCHETYPES = [
  {
    key: 'autolet',
    label: 'Auto-create from runbook / workflow / URL / process files',
    description: 'AutoLet · 7-stage pipeline mints + protects',
    icon: Sparkles,
    iconTone: 'trust',
    requiresSuperAdmin: false,
  },
  {
    key: 'let',
    label: 'Create native Agentlet',
    description: 'Let · Hand-authored with B-D-A lifecycle',
    icon: Bot,
    iconTone: 'action',
    requiresSuperAdmin: false,
  },
  {
    key: 'extelet',
    label: 'Register external agent',
    description: 'ExteLet · GEL connector + verification + B-D-A',
    icon: Globe,
    iconTone: 'cyan',
    requiresSuperAdmin: false,
  },
  {
    key: 'syslet',
    label: 'Provision system agent',
    description: 'SysLet · Platform-managed system agent',
    icon: Cpu,
    iconTone: 'muted',
    /* SysLets are platform-managed system agents (housekeeping / bootstrapped
     * by the platform operator). They are NOT user-authored through the tenant
     * "New Agentlet" create flow — the create endpoint can't even mint one
     * (no isSystemAgent on CreateAgentRequest). Kept in the taxonomy for
     * display (archetypeShortLabel, inventory facet) but excluded from the
     * create menu. Restricting "who can create a SysLet" to Platform Admin is
     * therefore enforced by simply not offering it here. */
    availableInCreateMenu: false,
  },
];

/** Short display name per archetype key — the pill/badge label. One source so
 * the SysLet badge can never silently fall back to "Let" again. */
export const ARCHETYPE_SHORT_LABEL = {
  let: 'Let',
  autolet: 'AutoLet',
  extelet: 'ExteLet',
  syslet: 'SysLet',
};

export function archetypeShortLabel(key) {
  return ARCHETYPE_SHORT_LABEL[key] || 'Let';
}

/**
 * The ONE canonical create surface. Every off-page entry point navigates
 * here; the Agentlets inventory page reads `?intent=create&archetype=<key>`
 * on mount and opens the matching flow in place (native modal for let/syslet,
 * AutoCreateLauncher for autolet, ExteletRegistrationWizard for extelet).
 * No more per-page divergent routes (the old /gel-agents?intent=register
 * special case is folded in here).
 */
export function agentCreateRoute(key) {
  return `/agents?intent=create&archetype=${encodeURIComponent(key)}`;
}

/**
 * Build the MenuDropdown `items` for the New-Agentlet menu. Only user-authored
 * archetypes appear (autolet / let / extelet). SysLet is platform-managed and
 * excluded by `availableInCreateMenu: false`.
 *
 * @param onPick  (archetypeKey) => void — what to do when an archetype is
 *                chosen. On the Agentlets page this is openCreate(); off page
 *                it's navigate(agentCreateRoute(key)).
 */
export function buildNewAgentletItems({ onPick }) {
  const items = [{ id: 'hdr', header: 'Choose archetype' }];
  AGENT_ARCHETYPES
    .filter((a) => a.availableInCreateMenu !== false)
    .forEach((a) => {
      const Icon = a.icon;
      items.push({
        id: a.key,
        label: a.label,
        description: a.description,
        iconTone: a.iconTone,
        icon: <Icon size={14} />,
        onSelect: () => onPick(a.key),
      });
    });
  return items;
}
